type Activity = {
  metadata: {
    rating: string;
    expertId?: string;
    expertName?: string;
    userId?: string;
    userName?: string;
    email: string;
    profileImage: string;
    amount: string;
  };
  timestamp: {
    seconds: number;
  };
};

type AggregatedData = {
  name: string;
  email: string;
  totalSessions: number;
  lastActivity: string;
  totalEarnings: string;
  avgRating: string;
  totalRatings: number;
};

const formatStartTime = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const aggregateData = (
  activities: Activity[],
  role: "customer" | "expert"
): AggregatedData[] => {
  const aggregatedData = activities.reduce<
    Record<string, AggregatedData & { ratings: number[] }>
  >((acc, activity) => {
    const id =
      role === "customer"
        ? activity.metadata.userId!
        : activity.metadata.expertId!;
    const name =
      role === "customer"
        ? activity.metadata.userName!
        : activity.metadata.expertName!;

    if (!acc[id]) {
      acc[id] = {
        name,
        email: activity.metadata.email,
        totalSessions: 0,
        lastActivity: "",
        totalEarnings: "0",
        avgRating: "0",
        totalRatings: 0,
        ratings: [],
      };
    }

    acc[id].totalSessions += 1;
    acc[id].ratings.push(parseFloat(activity.metadata.rating));
    acc[id].lastActivity = formatStartTime(activity.timestamp.seconds);
    acc[id].totalEarnings = (
      parseFloat(acc[id].totalEarnings) +
      parseFloat(activity.metadata.amount.replace("$", ""))
    ).toFixed(2);
    acc[id].totalRatings = acc[id].ratings.length;
    acc[id].avgRating = (
      acc[id].ratings.reduce((a, b) => a + b, 0) / acc[id].totalRatings
    ).toFixed(1);

    return acc;
  }, {});

  return Object.values(aggregatedData).map(({ ratings, ...rest }) => rest);
};

export const aggregateCustomerData = (activities: Activity[]) =>
  aggregateData(activities, "customer");
export const aggregateExpertData = (activities: Activity[]) =>
  aggregateData(activities, "expert");

export const getTimestamp = (createdAt: any) => {
  if (!createdAt) return 0;
  // Handles {seconds, nanoseconds} and possible missing nanoseconds
  const seconds = createdAt.seconds || 0;
  const nanos = createdAt.nanoseconds || 0;
  return seconds * 1000 + Math.floor(nanos / 1e6);
};
